import { deleteMaskAnnotationsOnFrame } from '@/modules/Editor/plugins/mask/utils/shared/deleteMaskAnnotationsOnFrame'
import type { View } from '@/modules/Editor/views/view'

import type { Bounds, RasterDataSnapshot } from './Raster'
import { Raster } from './Raster'
import type { RasterBufferAccessor } from './RasterBufferAccessor'
import { RasterTypes } from './rasterTypes'
import { encodeDenseRLE } from '@/modules/Editor/plugins/mask/rle/denseRle'
import { toRaw } from 'vue'
import { RasterFrames } from '@/modules/Editor/models/raster/RasterFrames'
import type { PartialRecord } from '@/core/helperTypes'
import { RasterManagerEvents } from '@/modules/Editor/eventBus'
import {
  RASTER_DELETE_LABEL_FROM_RASTER_END,
  RASTER_DELETE_LABEL_FROM_RASTER_START,
} from '@/performance/keys'
import { mark } from '@/performance/mark'
import { combineBounds } from '@/modules/Editor/plugins/mask/utils/shared/combineBounds'
import { isMaskAnnotation } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { isMaskAnnotationEmpty } from '@/modules/Editor/plugins/mask/utils/shared/isMaskAnnotationEmpty'

export type VideoBounds = Record<number, Bounds>

const EMPTY_PIXEL = 0

/**
 * A raster that stores a bunch of buffers per frame. Only frames with data are included,
 * to reduce memory footprint, and they are created when needed on demand.
 */
export class VideoRaster extends Raster {
  public idSyncedWithApi: string | null = null
  public readonly type: RasterTypes = RasterTypes.VIDEO
  public rasterRange: [number, number] = [0, 0]
  protected framesData: RasterFrames

  constructor(view: View) {
    super(view)
    this.framesData = new RasterFrames()
  }

  /**
   * @deprecated instead of getting all frame buffers, use `getFrameBufferAtFrame` passing the
   * index you are interested in
   */
  public get frameBuffers(): PartialRecord<number, Uint8Array> {
    const buffers: Record<number, Uint8Array> = {}
    this.framesData.frameIndexes.forEach((frameIndex: number) => {
      const buffer = this.framesData.bufferAt(frameIndex)
      if (buffer) {
        buffers[frameIndex] = buffer
      }
    })
    return buffers
  }

  public getFrameBufferAt(frameIndex: number): Uint8Array | undefined {
    return this.framesData.bufferAt(frameIndex)
  }

  /**
   * Takes a snapshot of the current state of the Raster.
   * This is only returning the snapshot, not setting it in any way.
   * Include the buffer for every frame, and data mappings to link annotations, labels and classes
   */
  public takeSnapshot(frameIndexes: number[]): RasterDataSnapshot {
    // Calculates the labels that are involved in the snapshot. Not all raster labels are used in
    // a partial frame range
    const labelsOnIndexes = frameIndexes.reduce((acc, frameIndex) => {
      this.getLabelsOnKeyframe(frameIndex).forEach((l) => acc.add(l))
      return acc
    }, new Set<number>())

    const snapshotData: RasterDataSnapshot = {
      denseRLE: {},
      maskAnnotationIdsMapping: structuredClone(toRaw(this.getAnnotationToLabelMapping())),
      maskAnnotationClassIdsMapping: structuredClone(toRaw(this.getAnnotationIdToClassIdMapping())),
      labelIndexToClassIdMapping: structuredClone(toRaw(this.getLabelIndexToClassIdMapping())),
      labelIndexToAnnotationIdMapping: structuredClone(
        toRaw(this.getLabelIndexToAnnotationMapping()),
      ),
      labelsInSnapshot: Array.from(labelsOnIndexes),
      maskAnnotations: JSON.parse(JSON.stringify(this.getAnnotations())),
    }

    frameIndexes.forEach((frameIndex) => {
      const frameBuffer = this.framesData.bufferAt(frameIndex)
      if (!frameBuffer) {
        // nothing to snapshot, skip
        return
      }
      snapshotData.denseRLE[frameIndex] = encodeDenseRLE(frameBuffer)
    })
    return snapshotData
  }

  /**
   * returns bounds that combine all the bounds from the passed label indexes
   */
  public getBoundsForLabels(labelIndexes: number[]): Bounds | undefined {
    return this.framesData.getLabelsBounds(labelIndexes)
  }

  /**
   * returns bounds that combine all the bounds from the passed label indexes
   */
  public getLabelsBounds(labelIndexes: number[]): Bounds | undefined {
    return this.framesData.getLabelsBounds(labelIndexes)
  }

  public getInvalidatedSubBuffer(): {
    x: number
    y: number
    width: number
    height: number
    data: Uint8Array
  } | null {
    // TODO: implement this to support WebGL rendering
    return null
  }

  public clearLabelsOnKeyframe(keyframe: number): void {
    this.framesData.removeLabelsAt(keyframe)
  }

  public setLabelOnKeyframe(labelIndex: number, keyframe: number, bounds: Bounds): void {
    this.framesData.setLabelAt(keyframe, labelIndex, bounds)
    this.validateRasterRange()
  }

  public deleteLabelOnKeyframe(labelIndex: number, keyframe: number): void {
    this.framesData.removeLabelsAt(keyframe, [labelIndex])
    this.validateRasterRange()
  }

  public getLabelsOnKeyframe(keyframe: number): number[] {
    return this.framesData.labelsOnFrame(keyframe)
  }

  public getKeyframesForAnnotationIds(annotationIds: string[]): number[] {
    const allFrames = new Set<number>()
    annotationIds.forEach((id) => {
      const labelIndex = this.getLabelIndexForAnnotationId(id)
      if (!labelIndex) {
        return
      }
      const frames = this.getLabelKeyframes(labelIndex)
      frames.forEach((frame) => allFrames.add(frame))
    })
    const sortedLabelFrames = Array.from(allFrames).sort((a, b) => a - b)
    return sortedLabelFrames
  }

  /** Get all keyframes where a label is present **/
  public getLabelKeyframes(labelIndex: number): number[] {
    return this.framesData.keyframesWithLabel(labelIndex)
  }

  public hasLabel(labelIndex: number): boolean {
    return this.framesData.keyframesWithLabel(labelIndex).length > 0
  }

  /** Returns the range of frames where the given label is defined **/
  public getLabelRange(labelIndex: number): [number, number] {
    const boundsPerFrame = this.framesData.getLabelBounds(labelIndex)
    if (!boundsPerFrame) {
      console.error('bounds per frame is undefined')
      return [0, 0]
    }

    const keyframes = Object.keys(boundsPerFrame).map(Number)

    if (keyframes.length === 0) {
      return [0, 0]
    }

    const first = Math.min(...keyframes)
    const last = Math.max(...keyframes)

    return [first, last]
  }

  /**
   * Returns an array of segments where the given label is not defined.
   * It works in the same way as annotation frame segments, where the upper bound is not
   * inclusive, so if frames 2 and 3 are empty, the result will be [[2, 4]].
   **/
  public getFramesWithoutLabel(labelIndex: number): [number, number][] {
    const framesWithoutLabel: [number, number][] = []
    const keyframes = this.framesData.keyframesWithLabel(labelIndex)

    let emptyRange: [number, number] | undefined
    for (let i = Math.min(...keyframes); i < Math.max(...keyframes); i++) {
      if (!keyframes.includes(i)) {
        // If we have an empty range, we should go on and check next frame
        if (emptyRange) {
          continue
        }

        // Let's create the empty frame object for the current frame range
        emptyRange = [i, -1]
        continue
      }

      // When keyframe has the label, and an empty range was previously found, then when finalise
      // it and add to the list
      if (emptyRange) {
        emptyRange[1] = i
        framesWithoutLabel.push(emptyRange)
        emptyRange = undefined
      }
    }

    // When the last frame is empty, then we need to close the range and add it to the list
    if (emptyRange) {
      emptyRange[1] = Math.max(...keyframes)
      framesWithoutLabel.push(emptyRange)
    }

    return framesWithoutLabel
  }

  public getVideoBoundsForLabelIndex(labelIndex: number): VideoBounds {
    return this.framesData.getLabelBounds(labelIndex)
  }

  public getVideoBoundsForLabelIndexForFrame(
    labelIndex: number,
    keyframe: number,
  ): Bounds | undefined {
    return this.framesData.getLabelBounds(labelIndex)[keyframe]
  }

  protected deleteBoundsForLabelIndex(): void {}

  /**
   * When we first create masks, they are created as image annotations.
   * As such we need a direct path to the active buffer for this first time a mask is drawn.
   */
  public getBufferForImageSerialization(): Uint8Array | undefined {
    return this.framesData.bufferAt(this.view.currentFrameIndex)
  }

  /**
   * Gets a buffer in the passed frameIndex for editing:
   *
   * The intention here is that we always get an editable buffer for this frame, even if
   * this means we create a new keyframe as a side effect (ready for drawing on).
   *
   * If the buffer exists for the current frame, just return it.
   * If the buffer doesn't exist for the current frame, create a blank one.
   */
  public getBufferForEdit(frameIndex: number): RasterBufferAccessor {
    const { width, height } = this

    const currentFrameBuffer =
      this.framesData.bufferAt(frameIndex) || this.createNewKeyframe(frameIndex)

    return {
      get(x: number, y: number): number {
        return currentFrameBuffer[y * width + x]
      },
      set(x: number, y: number, val: number): void {
        currentFrameBuffer[y * width + x] = val
      },
      getBuffer(): Uint8Array {
        return currentFrameBuffer
      },
      getSharedBuffer(): SharedArrayBuffer | ArrayBuffer {
        return currentFrameBuffer.buffer
      },
      width,
      height,
    }
  }

  /**
   * Gets the current buffer for editing
   */
  public getActiveBufferForEdit(): RasterBufferAccessor {
    const { currentFrameIndex } = this.view

    return this.getBufferForEdit(currentFrameIndex)
  }

  /**
   * Gets the current buffer for display:
   *
   * The intention here is that if we do not have a buffer for display on this frame
   * we display whatever the last frame was in the timeline as a preview.
   *
   * If we are outside the range, return undefined
   *
   * If the frame exists for the current image, just return it.
   */
  public getActiveBufferForRender(): RasterBufferAccessor | undefined {
    const { currentFrameIndex } = this.view
    const { width, height } = this

    // If we are past the upper range, return undefined
    if (this.rasterRange[1] < currentFrameIndex) {
      return
    }

    const currentFramBuffer = this.framesData.bufferAt(currentFrameIndex)

    if (currentFramBuffer === undefined) {
      return
    }

    return {
      get(x: number, y: number): number {
        return currentFramBuffer[y * width + x]
      },
      set(x: number, y: number, val: number): void {
        currentFramBuffer[y * width + x] = val
      },
      getBuffer(): Uint8Array {
        return currentFramBuffer
      },
      getSharedBuffer(): SharedArrayBuffer | ArrayBuffer {
        return currentFramBuffer.buffer
      },
      width,
      height,
    }
  }

  public setActiveBufferForFrame(buffer: Uint8Array, frameIndex: number): void {
    this.framesData.setBufferAt(frameIndex, buffer)
  }

  /**
   * Updates the `rasterRange` array to the latest range
   * A possible improvement is to get rid of `rasterRange` completely and instead have a method
   * to return the current raster range dynamically, so we have one less state to keep track of.
   */
  public validateRasterRange(): void {
    this.rasterRange = [...this.framesData.range]
    RasterManagerEvents.videoRasterRangeUpdated.emit({ viewId: this.view.id }, this.id)
  }

  // TODO: return string[] to avoid cycling through large number of keys
  public get rasterKeyframes(): number[] {
    return this.framesData.frameIndexes
  }

  /**
   * Returns an array of keyframes that have labels associated with them.
   * Any frame with label 0 (no annotation) is excluded.
   *
   * @returns {number[]} An array of keyframes.
   */
  public get rasterKeyframesWithLabels(): number[] {
    return this.framesData.framesWithLabels
  }

  public createNewKeyframe(newFrameIndex: number): Uint8Array {
    const newFrameBuffer: Uint8Array = new Uint8Array(this.size)

    this.setActiveBufferForFrame(newFrameBuffer, newFrameIndex)

    RasterManagerEvents.videoRasterKeyframeAdded.emit(
      { viewId: this.view.id },
      { rasterId: this.id, keyframe: newFrameIndex },
    )

    return newFrameBuffer
  }

  public mergeLabels(baseLabelIndex: number, ...labelIndexes: number[]): Promise<void> {
    if (this.getLabelKeyframes(baseLabelIndex).length === 0) {
      return Promise.resolve()
    }

    const changedAnnotationIds: string[] = []
    labelIndexes.forEach(async (labelIndex) => {
      const bounds = this.framesData.getLabelBounds(labelIndex)
      if (!bounds) {
        return
      }

      await this.updateLabelOnRaster(labelIndex, baseLabelIndex)

      const mergedAnnotationId = this.getAnnotationMapping(labelIndex)
      if (mergedAnnotationId) {
        changedAnnotationIds.push(mergedAnnotationId)
      }
      this.deleteAnnotationMapping(labelIndex)
    })

    this.validateRasterRange()
    this.invalidateAll(changedAnnotationIds)

    return Promise.resolve()
  }

  public deleteKeyframe(keyframe: number): { changedLabels: number[] } {
    const changedLabels = this.getLabelsOnKeyframe(keyframe)

    deleteMaskAnnotationsOnFrame(this.view, this, keyframe)
    this.framesData.removeKeyframe(keyframe)

    this.validateRasterRange()
    RasterManagerEvents.videoRasterKeyframeDeleted.emit(
      { viewId: this.view.id },
      { rasterId: this.id, keyframe },
    )

    return { changedLabels }
  }

  protected copyPreviousFrame(
    currentFrameIndex: number,
    previousKeyframeIndex: number,
  ): Uint8Array {
    const newFrameBuffer = new Uint8Array(this.size)
    const previousFrameBuffer = this.framesData.bufferAt(previousKeyframeIndex)

    if (previousFrameBuffer) {
      // Deep copy it's contents into the new buffer
      newFrameBuffer.set(previousFrameBuffer)
    }

    const previousFrameLabels = this.getLabelsOnKeyframe(previousKeyframeIndex)

    // Copy ranges from previous for all labels.
    previousFrameLabels.forEach((labelIndex) => {
      const ranges = this.getVideoBoundsForLabelIndex(labelIndex)

      const previousRange = ranges[previousKeyframeIndex]

      this.setLabelOnKeyframe(labelIndex, currentFrameIndex, structuredClone(previousRange))
    })

    return newFrameBuffer
  }

  public getPreviousKeyframeIndex(currentFrameIndex: number): number | undefined {
    for (let f = currentFrameIndex; f >= 0; f--) {
      const frameBuffer = this.framesData.bufferAt(f)

      if (frameBuffer) {
        return f
      }
    }
  }

  public async deleteLabelFromRaster(labelIndex: number): Promise<void> {
    mark(RASTER_DELETE_LABEL_FROM_RASTER_START)
    await this.updateLabelOnRaster(labelIndex, EMPTY_PIXEL)

    this.deleteAnnotationMapping(labelIndex)
    this.framesData.removeLabel(labelIndex)
    this.invalidateAll()
    mark(RASTER_DELETE_LABEL_FROM_RASTER_END)
  }

  protected updateLabelOnRaster(labelIndex: number, updatedLabelIndex: number): Promise<void> {
    const { width } = this

    const labelBounds = this.framesData.getLabelBounds(labelIndex)

    Object.keys(labelBounds).forEach((keyframe: string): void => {
      const frameIndex = Number(keyframe)
      const bounds = labelBounds[frameIndex]
      const buffer = this.framesData.bufferAt(frameIndex)

      if (!buffer) {
        return
      }

      if (bounds) {
        const xMin = bounds.topLeft.x
        const xMax = bounds.bottomRight.x
        const yMin = bounds.topLeft.y
        const yMax = bounds.bottomRight.y

        for (let y = yMin; y < yMax; y++) {
          for (let x = xMin; x < xMax; x++) {
            const pixelIndex = y * width + x

            if (buffer[pixelIndex] === labelIndex) {
              buffer[pixelIndex] = updatedLabelIndex
            }
          }
        }
      } else {
        const length = buffer.length

        for (let pixelIndex = 0; pixelIndex < length; pixelIndex++) {
          if (buffer[pixelIndex] === labelIndex) {
            buffer[pixelIndex] = updatedLabelIndex
          }
        }
      }

      if (updatedLabelIndex !== EMPTY_PIXEL) {
        const previousLabelIndexBounds =
          this.framesData.getLabelBounds(updatedLabelIndex)[frameIndex]
        this.framesData.setLabelAt(
          Number(keyframe),
          updatedLabelIndex,
          combineBounds([previousLabelIndexBounds || bounds, bounds]) || bounds,
        )
      }
    })

    this.deleteAnnotationMapping(labelIndex)
    this.framesData.removeLabel(labelIndex)
    this.validateRasterRange()
    this.invalidateAll()
    mark(RASTER_DELETE_LABEL_FROM_RASTER_END)

    return Promise.resolve()
  }

  public isLabelUsedOnOtherKeyframe(labelIndex: number, frameIndex: number): boolean {
    const labelKeyframes = this.framesData.keyframesWithLabel(labelIndex)
    return labelKeyframes.some((kf) => kf !== frameIndex)
  }

  public getFileSlotName(): string {
    return this.view.fileManager.slotName
  }

  cleanup(): void {
    super.cleanup()

    this.framesData.clear()
    this.cachedCanvas?.remove()
    delete this.cachedCanvas
  }

  /**
   * Checks and removes empty masks after raster operations for video rasters
   */
  public async checkAndRemoveEmptyMasks(
    labelsBeingOverwritten: number[],
    framesRange: number[] = [this.view.currentFrameIndex, this.view.currentFrameIndex],
  ): Promise<void> {
    const { annotationManager } = this.view
    const labelsToDelete: number[] = []
    const annotationsToRemove: string[] = []

    labelsBeingOverwritten.forEach((labelIndex: number) => {
      const annotationId = this.getAnnotationMapping(labelIndex)
      if (!annotationId) return

      const annotation = annotationManager.getAnnotation(annotationId)
      if (!annotation || !isMaskAnnotation(annotation)) return

      // Check if the mask is empty across all frames in the range
      let hasPixels = false
      for (let frameIndex = framesRange[0]; frameIndex <= framesRange[1]; frameIndex++) {
        if (!this.getLabelsOnKeyframe(frameIndex).includes(labelIndex)) {
          continue
        }

        const rasterBufferAccessor = this.getBufferForEdit(frameIndex)
        const bounds = this.getVideoBoundsForLabelIndexForFrame(labelIndex, frameIndex)

        if (!isMaskAnnotationEmpty(labelIndex, rasterBufferAccessor, bounds)) {
          hasPixels = true
          break
        }
      }

      if (!hasPixels) {
        labelsToDelete.push(labelIndex)
        annotationsToRemove.push(annotationId)
      }
    })

    // Delete labels from raster first (this updates the pixel data directly)
    for (const labelIndex of labelsToDelete) {
      for (let frameIndex = framesRange[0]; frameIndex <= framesRange[1]; frameIndex++) {
        await this.deleteLabelOnKeyframe(labelIndex, frameIndex)
      }
    }

    // Then remove annotations from annotation manager (this is just metadata cleanup)
    annotationsToRemove.forEach((annotationId) => {
      annotationManager.deleteAnnotation(annotationId)
    })

    return Promise.resolve()
  }
}
